import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';
import { DataService } from 'src/app/services/data.service';
import { MessageService } from 'primeng/api';

interface ImportRow {
  title: string;
  image_ref?: string;
  animation_triggers?: string;
  enabled?: string;
  env?: string;
  [key: string]: any;
}

interface ResolvedTrigger {
  name: string;
  option: string;
}

interface ResolvedRow {
  rowNum: number;
  title: string;
  payload: any | null;
  triggers: ResolvedTrigger[];
  warnings: string[];
}

interface RowResult {
  row: number;
  title: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
}

type Step = 'upload' | 'preview' | 'results';

@Component({
  selector: 'app-answers-bulk-import',
  templateUrl: './answers-bulk-import.component.html',
  styleUrls: ['./answers-bulk-import.component.sass'],
  providers: [MessageService],
})
export class AnswersBulkImportComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  step: Step = 'upload';
  parsedRows: ImportRow[] = [];
  resolvedRows: ResolvedRow[] = [];
  results: RowResult[] = [];
  isLoading: boolean = false;
  isImporting: boolean = false;
  progress: string = '';
  pendingImportConfirm: boolean = false;

  constructor(
    private papa: Papa,
    private commonEntityService: CommonEntityService,
    private dataService: DataService,
    private messageService: MessageService,
  ) {}

  onFileChange(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    this.reset();

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.papa.parse(e.target.result, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (res: any) => {
          const rows: ImportRow[] = res.data;
          if (!rows.length || !('title' in rows[0])) {
            this.messageService.add({ severity: 'error', summary: 'Invalid CSV', detail: 'CSV must have a "title" column.' });
            return;
          }
          this.parsedRows = rows;
        },
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Parse Error', detail: err.message });
        },
      });
    };
    reader.readAsText(file);
  }

  async preview() {
    if (!this.parsedRows.length) return;
    this.isLoading = true;

    try {
      await this.resolveRows();
    } finally {
      this.isLoading = false;
    }

    this.step = 'preview';
  }

  private async resolveRows() {
    const [icons, animationTriggers, existingAnswers] = await Promise.all([
      this.dataService.getAllOfTypeAsync('miscellaneous-build', { query: {}, select: 'name _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('animation-triggers', { query: {}, select: 'name id options _id' }).catch(() => []),
      this.loadExistingTitles(),
    ]);

    const iconMap = new Map<string, string>((icons || []).map((r: any) => [r.name?.toLowerCase(), r._id]));
    const triggerMap = new Map<string, any>((animationTriggers || []).map((t: any) => [t.name?.toLowerCase(), t]));

    const seenTitles = new Map<string, number>();

    this.resolvedRows = this.parsedRows.map((row, i) => {
      const payload: any = {};
      const triggers: ResolvedTrigger[] = [];
      const warnings: string[] = [];

      if (!row.title?.trim()) {
        return { rowNum: i + 1, title: '', payload: null, triggers: [], warnings: ['Missing title'] };
      }

      const title = row.title.trim();
      payload.title = title;

      const key = title.toLowerCase();
      const firstSeenAt = seenTitles.get(key);
      if (firstSeenAt) warnings.push(`Duplicate title in CSV (also row ${firstSeenAt})`);
      else seenTitles.set(key, i + 1);
      if (existingAnswers.has(key)) warnings.push(`An answer titled "${title}" already exists`);

      if (row.image_ref?.trim()) {
        const id = iconMap.get(row.image_ref.trim().toLowerCase());
        if (id) payload.image_ref = id;
        else warnings.push(`image_ref: "${row.image_ref}" not found`);
      }

      if (row.animation_triggers?.trim()) {
        const map: { [triggerId: string]: string } = {};
        const pairs = row.animation_triggers.split('|').map((s: string) => s.trim()).filter(Boolean);

        for (const pair of pairs) {
          const separator = pair.indexOf(':');
          if (separator < 1) {
            warnings.push(`animation_triggers: "${pair}" is not in Trigger:Option format`);
            continue;
          }
          const name = pair.slice(0, separator).trim();
          const option = pair.slice(separator + 1).trim();
          const trigger = triggerMap.get(name.toLowerCase());

          if (!trigger) {
            warnings.push(`animation_triggers: trigger "${name}" not found`);
            continue;
          }
          if (trigger.id == null) {
            warnings.push(`animation_triggers: trigger "${name}" has no id`);
            continue;
          }
          if (!option) {
            warnings.push(`animation_triggers: no option given for "${name}"`);
            continue;
          }
          if (!trigger.options?.length) {
            warnings.push(`animation_triggers: trigger "${name}" has no options configured`);
            continue;
          }
          if (!trigger.options.includes(option)) {
            warnings.push(`animation_triggers: "${option}" is not an option of "${name}"`);
            continue;
          }
          if (map[String(trigger.id)] != null) {
            warnings.push(`animation_triggers: trigger "${name}" set more than once`);
            continue;
          }

          map[String(trigger.id)] = option;
          triggers.push({ name: trigger.name, option });
        }

        if (Object.keys(map).length) payload.animationTriggers = map;
      }

      payload.enabled = row.enabled?.trim().toLowerCase() === 'false' ? false : true;

      if (row.env?.trim()) {
        payload.env = row.env.split(',').map((e: string) => e.trim()).filter(Boolean);
      }

      return { rowNum: i + 1, title, payload, triggers, warnings };
    });
  }

  private async loadExistingTitles(): Promise<Set<string>> {
    try {
      const res = await this.dataService.findManyDynamic('answers', {
        query: {},
        select: 'title _id',
        pageSize: 10000,
        autopopulate: false,
        virtuals: false,
      });
      const list = Array.isArray(res) ? res : (res?.records ?? res?.data ?? []);
      return new Set<string>(list.map((a: any) => a.title?.trim().toLowerCase()).filter(Boolean));
    } catch {
      return new Set<string>();
    }
  }

  get warnRowNums(): number[] {
    return this.resolvedRows.filter(r => r.payload && r.warnings.length).map(r => r.rowNum);
  }

  import() {
    if (this.warnRowNums.length && !this.pendingImportConfirm) {
      this.pendingImportConfirm = true;
      return;
    }
    this.pendingImportConfirm = false;
    this.doImport();
  }

  cancelImportConfirm() {
    this.pendingImportConfirm = false;
  }

  private async doImport() {
    if (!this.resolvedRows.length) return;
    this.isImporting = true;
    this.results = [];

    for (let i = 0; i < this.resolvedRows.length; i++) {
      const { rowNum, title, payload, warnings } = this.resolvedRows[i];
      this.progress = `${i + 1} / ${this.resolvedRows.length}`;

      if (!payload) {
        this.results.push({ row: rowNum, title, status: 'skipped', message: 'Missing title' });
        continue;
      }

      try {
        const created = await this.commonEntityService.createDynamicEntity('answers', payload);
        this.results.push({
          row: rowNum, title, status: 'success',
          message: warnings.length ? `Warnings: ${warnings.join('; ')}` : (created?.id ? `ID: ${created.id}` : undefined),
        });
      } catch (e: any) {
        this.results.push({ row: rowNum, title, status: 'error', message: e?.error?.message || 'Failed to create' });
      }
    }

    this.isImporting = false;
    this.progress = '';
    this.step = 'results';
  }

  getTriggerSummary(triggers: ResolvedTrigger[]): string {
    return triggers.map(t => `${t.name}: ${t.option}`).join(' | ');
  }

  get validRowCount()   { return this.resolvedRows.filter(r => r.payload).length; }
  get warnRowCount()    { return this.resolvedRows.filter(r => r.payload && r.warnings.length).length; }
  get invalidRowCount() { return this.resolvedRows.filter(r => !r.payload).length; }
  get successCount()    { return this.results.filter(r => r.status === 'success').length; }
  get errorCount()      { return this.results.filter(r => r.status === 'error').length; }
  get skippedCount()    { return this.results.filter(r => r.status === 'skipped').length; }

  downloadTemplate() {
    const headers = 'title,image_ref,animation_triggers,enabled,env';
    const example = '"Blue",,"Head Animation:Nod|Body Animation:Idle",true,"dev,qa,prod"';
    const blob = new Blob([headers + '\n' + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'answers_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  back() { this.step = 'upload'; this.resolvedRows = []; this.pendingImportConfirm = false; }

  close() {
    this.visibleChange.emit(false);
    this.reset();
  }

  private reset() {
    this.step = 'upload';
    this.parsedRows = [];
    this.resolvedRows = [];
    this.results = [];
    this.progress = '';
    this.pendingImportConfirm = false;
  }
}
